// Hook for the dupe-count notification bubble + per-partner breakdown.
//
// Returns the count of strong+probable duplicate pairs across the
// signed-in user's cellar. Used by UserMenu to surface a bubble on the
// avatar + a badge on the "Share & Connections" entry so users know
// where to go to resolve them.
//
// Counts both flavors of duplicate:
//   - cross-user (between household members in a shared cellar)
//   - self-self (same user added the same bottle twice)
//
// Two hooks exposed:
//   - useDupeCount(user)     → number  (just the total — for the avatar bubble)
//   - useDupeBreakdown(user) → { total, self, byPartner: {uid: count} }
//                              (lets Sharing gate per-row CTAs to non-zero)
//
// Implementation: loads the user's cellar wines via the cellarId infra,
// then runs detectDuplicatePairs over the whole set. Module-level cache
// TTL of 60s keeps repeated mounts from re-querying Firestore.

import { useEffect, useState } from 'react'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from './../firebase.js'
import { ensureCellarForUser } from './cellars.js'
import { detectDuplicatePairs } from './cellarMerge.js'

const CACHE_TTL_MS = 60_000
const EMPTY_BREAKDOWN = { total: 0, self: 0, byPartner: {} }
let _cache = null  // { uid, breakdown, expiresAt }
let _subscribers = []   // Array of () => void; one per mounted hook consumer

export function invalidateDupeCountCache() {
  _cache = null
  // Notify every mounted consumer to re-fetch. Without this the bubble
  // would stay frozen at the pre-merge count until either the user
  // navigates away + back, or the 60s TTL elapses.
  for (const fn of _subscribers) {
    try { fn() } catch (_) {}
  }
}

async function loadBreakdown(user) {
  if (!user?.uid) return EMPTY_BREAKDOWN
  // Resolve the user's cellarId (reuses cellars.js cache).
  const cellarId = await ensureCellarForUser(user)
  if (!cellarId) return EMPTY_BREAKDOWN

  // Load the shared cellar's wines. Single indexed query — cheap on
  // its own.
  const winesSnap = await getDocs(
    query(collection(db, 'wines'), where('cellarId', '==', cellarId))
  )
  const wines = winesSnap.docs.map(d => ({ id: d.id, ...d.data() }))
  if (wines.length < 2) return EMPTY_BREAKDOWN

  const buckets = detectDuplicatePairs(wines, user.uid)
  // Strong + probable = "needs attention" set. Possible defaults to
  // keep-both in the merge UI so it doesn't demand a review pass.
  const interesting = [...buckets.strong, ...buckets.probable]

  let self = 0
  const byPartner = {}
  for (const pair of interesting) {
    if (pair.selfSelf) {
      // Both sides are caller's wines — resolved via the solo
      // "Manage duplicates" entry point in Sharing.
      self++
      continue
    }
    // Cross-user pair: figure out the partner uid (the side that's not me).
    const aUid = pair.mine?.userId
    const bUid = pair.theirs?.userId
    let partnerUid = null
    if (aUid && aUid !== user.uid) partnerUid = aUid
    else if (bUid && bUid !== user.uid) partnerUid = bUid
    if (!partnerUid) continue
    byPartner[partnerUid] = (byPartner[partnerUid] || 0) + 1
  }
  const total = self + Object.values(byPartner).reduce((s, n) => s + n, 0)
  return { total, self, byPartner }
}

// Shared loader used by both hooks. Caches at module level so multiple
// mounted consumers (UserMenu + Sharing on the same page) hit Firestore
// once per TTL window.
function useBreakdownInternal(user) {
  const [breakdown, setBreakdown] = useState(() => {
    if (_cache && _cache.uid === user?.uid && Date.now() < _cache.expiresAt) {
      return _cache.breakdown
    }
    return EMPTY_BREAKDOWN
  })
  // bumpKey: incremented via invalidateDupeCountCache() from the merge
  // confirm path. Re-runs the load effect below so a freshly-merged user
  // sees the new (lower) count immediately instead of waiting for TTL.
  const [bumpKey, setBumpKey] = useState(0)

  useEffect(() => {
    const onInvalidate = () => setBumpKey(k => k + 1)
    _subscribers.push(onInvalidate)
    return () => {
      _subscribers = _subscribers.filter(fn => fn !== onInvalidate)
    }
  }, [])

  useEffect(() => {
    if (!user?.uid) { setBreakdown(EMPTY_BREAKDOWN); return }
    let cancelled = false

    if (_cache && _cache.uid === user.uid && Date.now() < _cache.expiresAt) {
      setBreakdown(_cache.breakdown)
      return
    }

    loadBreakdown(user)
      .then(b => {
        if (cancelled) return
        _cache = { uid: user.uid, breakdown: b, expiresAt: Date.now() + CACHE_TTL_MS }
        setBreakdown(b)
      })
      .catch(err => {
        if (typeof console !== 'undefined') console.warn('[dupeCount] load failed:', err)
      })

    return () => { cancelled = true }
  }, [user?.uid, bumpKey])

  return breakdown
}

/**
 * React hook returning just the total dupe count for the signed-in user.
 * Used by UserMenu for the avatar bubble.
 */
export function useDupeCount(user) {
  return useBreakdownInternal(user).total
}

/**
 * React hook returning the full breakdown — total + self-self + per-partner.
 * Used by Sharing to gate per-row CTAs to actual work-to-do.
 */
export function useDupeBreakdown(user) {
  return useBreakdownInternal(user)
}
